import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm, Space } from 'antd';
import { customerApi, type CustomerDTO, type CustomerAddressDTO } from '../api/kfc';

const CustomerManage: React.FC = () => {
  const [list, setList] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    customerApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载客户列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ addresses: [{}] });
    setModalOpen(true);
  };

  const openEdit = (r: CustomerDTO) => {
    setEditingId(r.id ?? null);
    const values = {
      ...r,
      addresses: r.addresses?.length ? r.addresses : [{}],
    };
    form.setFieldsValue(values);
    setModalOpen(true);
  };

  const onFinish = (values: Partial<CustomerDTO> & { addresses?: CustomerAddressDTO[] }) => {
    const { addresses, ...rest } = values;
    const payload = { ...rest, addresses: addresses?.filter((a) => a.contactName || a.address || a.phone) ?? [] };
    const submit = editingId
      ? customerApi.update(editingId, payload)
      : customerApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const doDelete = (id: string) => {
    customerApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card title="客户列表" extra={<Button type="primary" onClick={openAdd}>新增客户</Button>}>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          columns={[
            { title: '客户标识', dataIndex: 'customerCode', width: 120 },
            { title: '客户名称', dataIndex: 'name', width: 140 },
            { title: '域名', dataIndex: 'domain', width: 140, ellipsis: true },
            { title: '邮箱', dataIndex: 'email', width: 160, ellipsis: true },
            { title: '电话', dataIndex: 'phone', width: 120 },
            {
              title: '操作',
              width: 180,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定删除该客户吗？"
                    onConfirm={() => r.id != null && doDelete(r.id)}
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑客户' : '新增客户'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="customerCode" label="客户标识" rules={[{ required: true, message: '请输入客户标识' }]}>
            <Input placeholder="唯一标识" disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="domain" label="域名">
            <Input placeholder="为空则使用 {客户标识}.com" />
          </Form.Item>
          <Form.Item name="name" label="客户名称" rules={[{ required: true, message: '请输入客户名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="客户邮箱">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="客户电话">
            <Input />
          </Form.Item>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>客户地址（可选）</div>
          <Form.List name="addresses">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...rest} name={[name, 'contactName']} label="联系人" style={{ marginBottom: 0 }}>
                      <Input placeholder="联系人" style={{ width: 100 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'phone']} label="电话" style={{ marginBottom: 0 }}>
                      <Input placeholder="电话" style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'address']} label="地址" style={{ marginBottom: 0 }}>
                      <Input placeholder="地址" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'postalCode']} label="邮编" style={{ marginBottom: 0 }}>
                      <Input placeholder="邮编" style={{ width: 80 }} />
                    </Form.Item>
                    <Button type="text" onClick={() => remove(name)}>
                      删除
                    </Button>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + 添加地址
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerManage;
